import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Pagination } from 'semantic-ui-react';

import { Loader } from '@playbookux/playbook-web-core';

import client from '../../../../../../core/services/graphql';

import {
  ContainerAnswers,
  HeaderContainer,
  Search,
  Title,
  ContainerTitle,
  QuestionOrder,
  Answer,
  TesterName,
  WrittenResponse,
  NoResults,
  CenteredDiv,
} from './styles';
import { GET_WRITTEN_PAGINATED } from './graphql';

const WrittenChart = (props) => {
  const LIMIT = 20;
  const {
    data: dataWritten,
    title,
    segmentId,
    projectId,
    testerIds,
  } = props;

  const [search, setSearch] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [hasCalledAPI, setHasCalledAPI] = useState(false);
  const componentRef = useRef(null);

  const writtenPaginated = async (offset, searchValue) => {
    try {
      setLoading(true);
      const { data } = await client.getClient().query({
        query: GET_WRITTEN_PAGINATED,
        variables: {
          projectId,
          questionId: dataWritten.id,
          limit: LIMIT,
          offset: offset || 0,
          segmentId,
          search: searchValue || '',
          testerIds,
        },
      });
      setLoading(false);
      setResults(data.analytics.surveysAnalyticsWritten);
    } catch (e) {
      setLoading(false);
      setError(true);
    }
  };

  useEffect(() => {
    if (dataWritten) {
      writtenPaginated(0, search);
    }
  }, [dataWritten]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(_.debounce((value) => {
    writtenPaginated(0, value);
  }, 300), [segmentId, projectId, dataWritten.id]);

  /**
   * code from copilot :/
   * This method is to call writtenPaginated method when the component enters the viewport
   */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // Check if the element is on screen and the API hasn't been called yet
        if (entry.isIntersecting && !hasCalledAPI) {
          // Call your API here
          const fetchData = async () => {
            if (dataWritten) {
              await writtenPaginated();
            }
          };
          fetchData();
          setHasCalledAPI(true);
          // Optionally, disconnect the observer after the API call
          observer.disconnect();
        }
      },
      {
        root: null, // relative to the viewport
        threshold: 0.1, // trigger when 10% of the element is in view
      },
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasCalledAPI]); // Depend on hasCalledAPI to avoid unnecessary re-observations

  if (error) {
    return <Title>Error</Title>;
  }

  const handleSearchAnswer = (event) => {
    const { value } = event.target;
    setSearch(value === ' ' ? _.trim(value) : value);
    debouncedSearch(value);
  };

  const onPageChange = async (event, data) => {
    const pageSelected = data.activePage;
    await writtenPaginated((pageSelected - 1) * LIMIT, search);
  };

  return (
    <ContainerAnswers ref={componentRef}>
      <HeaderContainer>
        <ContainerTitle>
          <QuestionOrder>{`Question ${dataWritten.questionOrder + 1}`}</QuestionOrder>
          <Title>{`${dataWritten.label} (${title})`}</Title>
        </ContainerTitle>

        <Search
          placeholder="Search written responses"
          input={{
            icon: 'search',
            iconPosition: 'left',
          }}
          loading={false}
          onSearchChange={handleSearchAnswer}
          open={false}
          value={search}
          width="390px"
        />

      </HeaderContainer>

      <ContainerAnswers>
        {
          loading && (
            <Loader data-loader="written-chart-loader" />
          )
        }
        {
          !loading && results?.data.map((response) => (
            <Answer>
              <TesterName>{response.firstName}</TesterName>
              <WrittenResponse>{response.valueText}</WrittenResponse>
            </Answer>
          ))
        }
        {
          !loading && _.isEmpty(results?.data) && (
            <NoResults>No results found.</NoResults>
          )
        }
      </ContainerAnswers>
      <CenteredDiv>
        <Pagination
          defaultActivePage={1}
          totalPages={Math.ceil(results?.total / LIMIT)}
          onPageChange={onPageChange}
          ellipsisItem={null}
          size="mini"
        />
      </CenteredDiv>
    </ContainerAnswers>
  );
};

WrittenChart.defaultProps = {
  segmentId: '',
  testerIds: null,
};

WrittenChart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  segmentId: PropTypes.string,
  projectId: PropTypes.string.isRequired,
  testerIds: PropTypes.arrayOf(PropTypes.string),
};

export default React.memo(WrittenChart);
